import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Plus,
  Edit,
  Save,
  X,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertTriangle,
  Building,
  User,
  CheckCircle,
  Target,
} from "lucide-react"
import { HRService } from "@/features/hr"
import { OverrideRateService } from "@/features/hr"
import { useCampaign } from "@/features/campaigns"
import { useAuth } from "@/features/auth"
import type {
  OverrideRate,
  OverrideRateStats,
  OverrideRateFilters,
  OverrideRateValidationResult,
} from "@/features/hr"
import type { Organization } from "@/features/organizations"
import type { Campaign } from "@/features/campaigns"
import type { Agent } from "@/features/auth"
import { OverrideRateShimmerTable } from "@/shared/components"

interface OverrideRateManagerProps {
  onError: (error: string) => void
}

interface NewOverrideRateForm {
  organizationId: string
  campaignId: string
  agentId: string
  overrideRate: number
  jobType: "SALES_AGENT" | "LEAD_GENERATION" | "EXECUTIVE_AGENT" | "MASTER_ORGANIZATION"
  enableOverrideTier: boolean
  volumeTier1Min: number
  volumeTier1Max: number
  tier1PayRate: number
  volumeTier2Min: number
  volumeTier2Max: number
  tier2PayRate: number
  volumeTier3Min: number
  volumeTier3Max: number
  tier3PayRate: number
  isActive: boolean
}

interface FilterState {
  search: string
}

export default function OverrideRateManager({ onError }: OverrideRateManagerProps) {
  const { selectedCampaign } = useCampaign()
  const { user } = useAuth()
  const [rates, setRates] = useState<OverrideRate[]>([])
  const [stats, setStats] = useState<OverrideRateStats | null>(null)
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [ratesLoading, setRatesLoading] = useState(false)
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingRate, setEditingRate] = useState<string | null>(null)
  const [editingData, setEditingData] = useState<Partial<OverrideRate>>({})
  const [validationResult, setValidationResult] = useState<OverrideRateValidationResult | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [errorToast, setErrorToast] = useState<string | null>(null)

  // Filter organizations based on user role
  const organizations = useMemo(() => {
    if (!user || !allOrganizations.length) return []
    
    // Filter out FMO organizations for all users
    const filteredOrgs = allOrganizations.filter(org => 
      !org.name.toLowerCase().includes('fmo') && 
      !org.name.toLowerCase().includes('sample fmo')
    )
    
    switch (user.role) {
      case 'fmo_ceo':
        // FMO CEO can only see EM organizations
        return filteredOrgs.filter(org => org.type === 'EM')
      case 'hr_manager':
        // HR Manager can see DM, CM, BM, AM organizations
        return filteredOrgs.filter(org => 
          ['DM', 'CM', 'BM', 'AM'].includes(org.type)
        )
      default:
        // Default: show all non-FMO organizations
        return filteredOrgs
    }
  }, [user, allOrganizations])
  
  const [newRate, setNewRate] = useState<NewOverrideRateForm>({
    organizationId: "",
    campaignId: selectedCampaign?.id || "",
    agentId: "",
    overrideRate: 2,
    jobType: "SALES_AGENT",
    enableOverrideTier: false,
    volumeTier1Min: 1,
    volumeTier1Max: 300,
    tier1PayRate: 2,
    volumeTier2Min: 301,
    volumeTier2Max: 500,
    tier2PayRate: 3,
    volumeTier3Min: 501,
    volumeTier3Max: 1000,
    tier3PayRate: 5,
    isActive: true,
  })
  
  const [filters, setFilters] = useState<FilterState>({
    search: "",
  })

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)

  const allJobTypes = [
    { value: "SALES_AGENT", label: "Sales Agent" },
    { value: "LEAD_GENERATION", label: "Lead Generation" },
    { value: "EXECUTIVE_AGENT", label: "Executive Agent" },
    { value: "MASTER_ORGANIZATION", label: "Master Organization" },
  ]

  // Filter job types based on user role
  const jobTypes = useMemo(() => {
    if (user?.role === 'fmo_ceo') {
      return allJobTypes
    }
    // Hide MASTER_ORGANIZATION from non-FMO CEO users
    return allJobTypes.filter(jt => jt.value !== 'MASTER_ORGANIZATION')
  }, [user])

  // Set jobType to MASTER_ORGANIZATION for FMO CEO users
  useEffect(() => {
    if (user?.role === 'fmo_ceo' && newRate.jobType !== 'MASTER_ORGANIZATION') {
      setNewRate(prev => ({ ...prev, jobType: 'MASTER_ORGANIZATION' as any }))
    }
  }, [user?.role, newRate.jobType])

  // Helper function to show success toast
  const showSuccessToast = useCallback((message: string) => {
    setToastMessage(message)
    // Auto-clear toast after 3 seconds
    setTimeout(() => setToastMessage(null), 3000)
  }, [])

  // Helper function to show error toast
  const showErrorToast = useCallback((message: string) => {
    setErrorToast(message)
    // Auto-clear error after 5 seconds
    setTimeout(() => setErrorToast(null), 5000)
  }, [])

  // Load data on component mount
  useEffect(() => {
    loadData()
  }, [])

  // Load data when filters change
  useEffect(() => {
    loadRates()
  }, [filters, currentPage, itemsPerPage])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showAddForm) {
        setShowAddForm(false)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [showAddForm])

  // Auto-fill campaign when opening add form if a campaign is selected
  useEffect(() => {
    if (selectedCampaign?.id && showAddForm && !newRate.campaignId) {
      setNewRate(prev => ({ ...prev, campaignId: selectedCampaign.id }))
    }
  }, [selectedCampaign, showAddForm, newRate.campaignId])

  const loadData = async () => {
    try {
      await Promise.all([
        loadRates(),
        loadStats(),
        loadOrganizations(),
        loadCampaigns(),
        loadAgents()
      ])
    } catch (error) {
      console.error("Error loading data:", error)
      showErrorToast("Failed to load data")
    }
  }

  const loadRates = async () => {
    try {
      setRatesLoading(true)
      // Filter out empty strings and convert to proper types
      const cleanFilters: OverrideRateFilters = {
        page: currentPage,
        limit: itemsPerPage
      }
      
      if (filters.search && filters.search.trim()) {
        cleanFilters.search = filters.search.trim()
      }
      
      const result = await OverrideRateService.getOverrideRates(cleanFilters)
      setRates(result.overrideRates)
      setTotalPages(result.totalPages)
      setTotalItems(result.total)
    } catch (error) {
      console.error("Error loading override rates:", error)
      let errorMessage = "Failed to load override rates"
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        const status = apiError.response?.status
        const responseData = apiError.response?.data
        switch (status) {
          case 400: 
            errorMessage = responseData?.message || "Invalid request parameters"
            break
          case 401: 
            errorMessage = "Unauthorized - please log in again"
            break
          case 403: 
            errorMessage = "Forbidden - you don't have permission to view override rates"
            break
          case 404: 
            errorMessage = "Override rates endpoint not found"
            break
          case 500: 
            errorMessage = responseData?.message || "Server error - please try again later"
            break
          default: 
            errorMessage = responseData?.message || `API Error (${status}): ${apiError.response?.statusText || 'Unknown error'}`
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message
      }
      showErrorToast(errorMessage)
    } finally {
      setRatesLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      // Filter out empty strings and convert to proper types
      const cleanFilters: OverrideRateFilters = {}
      
      if (filters.search && filters.search.trim()) {
        cleanFilters.search = filters.search.trim()
      }
      
      const result = await OverrideRateService.getOverrideRateStats(cleanFilters)
      setStats(result)
    } catch (error) {
      console.error("Error loading stats:", error)
      // Don't show error for stats loading failure, just log it
      // This prevents unnecessary error messages for non-critical data
    }
  }

  const loadOrganizations = async () => {
    try {
      setOrganizationsLoading(true)
      const result = await HRService.getOrganizations()
      setAllOrganizations(result || [])
    } catch (error) {
      console.error("Error loading organizations:", error)
      // Don't show error for organizations loading failure, just log it
    } finally {
      setOrganizationsLoading(false)
    }
  }

  const loadCampaigns = async () => {
    try {
      const result = await HRService.getCampaigns()
      setCampaigns(result || [])
    } catch (error) {
      console.error("Error loading campaigns:", error)
      // Don't show error for campaigns loading failure, just log it
    }
  }

  const loadAgents = async () => {
    try {
      const result = await HRService.getAgents()
      setAgents(result || [])
    } catch (error) {
      console.error("Error loading agents:", error)
      // Don't show error for agents loading failure, just log it
    }
  }

  const handleAddRate = async () => {
    const requiredFields = [!newRate.organizationId, !newRate.campaignId]
    // Only require agentId for non-FMO CEO users
    if (user?.role !== 'fmo_ceo') {
      requiredFields.push(!newRate.agentId)
    }
    
    if (requiredFields.some(field => field)) {
      showErrorToast("Please fill in all required fields")
        return
      }
      
    try {
      setIsSubmitting(true)
        setValidationResult(null)
      
      const result = await OverrideRateService.createOverrideRate(newRate)
      
      if (result.success) {
        showSuccessToast("Override rate added successfully")
        setNewRate({
        organizationId: "",
        campaignId: "",
        agentId: "",
          overrideRate: 2,
        jobType: "SALES_AGENT",
        enableOverrideTier: false,
        volumeTier1Min: 1,
        volumeTier1Max: 300,
          tier1PayRate: 2,
        volumeTier2Min: 301,
        volumeTier2Max: 500,
          tier2PayRate: 3,
        volumeTier3Min: 501,
        volumeTier3Max: 1000,
          tier3PayRate: 5,
        isActive: true,
      })
        setShowAddForm(false)
        await loadRates()
        await loadStats()
      } else {
        setValidationResult({
          isValid: false,
          warnings: result.warnings || [],
          errors: result.errors || []
        })
      }
    } catch (error) {
      console.error("Error adding override rate:", error)
      let errorMessage = "Failed to add override rate"
      if (error && typeof error === 'object' && 'response' in error) {
          const apiError = error as any
        const status = apiError.response?.status
        const responseData = apiError.response?.data
        switch (status) {
          case 400: // Validation errors
            if (responseData?.errors && Array.isArray(responseData.errors)) {
              errorMessage = `${responseData.errors.join(', ')}`
            } else if (responseData?.message) {
              errorMessage = responseData.message
            } else {
              errorMessage = "Validation failed"
            }
            break
          case 409: // Duplicate entry
            errorMessage = responseData?.message || "Override rate already exists for this organization, campaign, and agent combination"
            break
          case 401: errorMessage = "Unauthorized - please log in again"; break
          case 403: errorMessage = "Forbidden - you don't have permission to perform this action"; break
          case 404: errorMessage = "Resource not found"; break
          case 500: errorMessage = responseData?.message || "Internal server error"; break
          default: errorMessage = responseData?.message || `API Error (${status}): ${apiError.response?.statusText || 'Unknown error'}`
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
          errorMessage = (error as Error).message
      }
      showErrorToast(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditRate = (rate: OverrideRate) => {
    setEditingRate(rate.id)
    setEditingData({
      overrideRate: rate.overrideRate,
      jobType: rate.jobType,
      enableOverrideTier: rate.enableOverrideTier,
      volumeTier1Min: rate.volumeTier1Min,
      volumeTier1Max: rate.volumeTier1Max,
      tier1PayRate: rate.tier1PayRate,
      volumeTier2Min: rate.volumeTier2Min,
      volumeTier2Max: rate.volumeTier2Max,
      tier2PayRate: rate.tier2PayRate,
      volumeTier3Min: rate.volumeTier3Min,
      volumeTier3Max: rate.volumeTier3Max,
      tier3PayRate: rate.tier3PayRate,
      isActive: rate.isActive
    })
    // Clear any existing validation errors
    setValidationResult(null)
  }

  const handleSaveEdit = async (id: string) => {
    try {
      setIsSubmitting(true)
      setValidationResult(null)
      
      const result = await OverrideRateService.updateOverrideRate(id, editingData)
      
      if (result.isValid) {
        showSuccessToast("Override rate updated successfully")
        setEditingRate(null)
        setEditingData({})
        await loadRates()
        await loadStats()
      } else {
        setValidationResult(result)
      }
    } catch (error) {
      console.error("Error updating override rate:", error)
      let errorMessage = "Failed to update override rate"
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        const status = apiError.response?.status
        const responseData = apiError.response?.data
        switch (status) {
          case 400: // Validation errors
            if (responseData?.errors && Array.isArray(responseData.errors)) {
              errorMessage = `Validation failed: ${responseData.errors.join(', ')}`
            } else if (responseData?.message) {
              errorMessage = responseData.message
            } else {
              errorMessage = "Validation failed"
            }
            break
          case 401: errorMessage = "Unauthorized - please log in again"; break
          case 403: errorMessage = "Forbidden - you don't have permission to perform this action"; break
          case 404: errorMessage = "Resource not found"; break
          case 500: errorMessage = responseData?.message || "Internal server error"; break
          default: errorMessage = responseData?.message || `API Error (${status}): ${apiError.response?.statusText || 'Unknown error'}`
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message
      }
      showErrorToast(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteRate = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this override rate?")) {
      return
    }

    try {
      setIsSubmitting(true)
      await OverrideRateService.deleteOverrideRate(id)
      showSuccessToast("Override rate deleted successfully")
      await loadRates()
      await loadStats()
    } catch (error) {
      console.error("Error deleting override rate:", error)
      let errorMessage = "Failed to delete override rate"
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        const status = apiError.response?.status
        const responseData = apiError.response?.data
        switch (status) {
          case 401: errorMessage = "Unauthorized - please log in again"; break
          case 403: errorMessage = "Forbidden - you don't have permission to perform this action"; break
          case 404: errorMessage = "Resource not found"; break
          case 500: errorMessage = responseData?.message || "Internal server error"; break
          default: errorMessage = responseData?.message || `API Error (${status}): ${apiError.response?.statusText || 'Unknown error'}`
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message
      }
      showErrorToast(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page when filters change
  }

  const clearFilters = () => {
    setFilters({
      search: "",
    })
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items)
    setCurrentPage(1)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const getJobTypeLabel = (jobType: string) => {
    return jobTypes.find(jt => jt.value === jobType)?.label || jobType
    }

    return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">Override Rates Management</h2>
          <p className="text-sm text-accent-600 mt-1">Define override rates for team sales performance</p>
        </div>
          <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary flex items-center justify-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Override Rate</span>
          </button>
        </div>



      {/* Validation Warnings/Errors */}
      {validationResult && (
        <div className={`card ${validationResult?.isValid ? 'border-yellow-200 bg-yellow-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-start space-x-2">
            <AlertTriangle className={`w-5 h-5 ${validationResult?.isValid ? 'text-yellow-600' : 'text-red-600'} mt-0.5`} />
          <div>
              <h3 className={`font-medium ${validationResult?.isValid ? 'text-yellow-800' : 'text-red-800'}`}>
                {validationResult?.isValid ? 'Warnings' : 'Validation Errors'}
              </h3>
              {validationResult?.warnings?.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-yellow-700">Warnings:</h4>
                  <ul className="list-disc list-inside text-sm text-yellow-700 mt-1">
                    {validationResult.warnings?.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    )) || []}
                  </ul>
                </div>
              )}
              {validationResult?.errors?.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-red-700">Errors:</h4>
                  <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                    {validationResult.errors?.map((error, index) => (
                      <li key={index}>{error}</li>
                    )) || []}
                  </ul>
            </div>
          )}
                </div>
              </div>
            </div>
          )}

      {/* Add Override Rate Modal */}
      {showAddForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAddForm(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-accent-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-accent-900">Add Override Rate</h3>
              <button 
                onClick={() => setShowAddForm(false)} 
                className="text-accent-400 hover:text-accent-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <h4 className="text-md font-semibold text-accent-900 mb-6">Override Rate Configuration</h4>
              
              {/* Main Configuration Row */}
              <div className={`grid grid-cols-1 gap-4 mb-6 ${user?.role === 'fmo_ceo' ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                    {/* Campaign - Read Only Display */}
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-1">Campaign *</label>
                      {selectedCampaign ? (
                        <div className="flex items-center space-x-2 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
                          <Target className="w-4 h-4 text-primary-600" />
                          <span className="text-sm font-medium text-primary-900">{selectedCampaign.name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-amber-800">No campaign selected</span>
                        </div>
                      )}
          </div>

                    {/* Organization */}
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-1">Organization *</label>
                      <select
                        className="input-field"
                    value={newRate.organizationId}
                    onChange={(e) => setNewRate({ ...newRate, organizationId: e.target.value })}
                        disabled={organizationsLoading}
                      >
                        <option value="">
                          {organizationsLoading 
                            ? 'Loading organizations...'
                            : organizations.length === 0 
                            ? user?.role === 'fmo_ceo' 
                              ? 'No EM organizations available' 
                              : user?.role === 'hr_manager'
                              ? 'No DM/CM/BM/AM organizations available'
                              : 'No organizations available'
                            : 'Select Organization'
                          }
                        </option>
                        {!organizationsLoading && organizations?.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name} ({org.type})
                          </option>
                    )) || []}
                      </select>
          </div>

                    {/* Salesman Name - Hidden for FMO CEO */}
                    {user?.role !== 'fmo_ceo' && (
              <div>
                      <label className="block text-sm font-medium text-accent-700 mb-1">Salesman Name *</label>
                      <select
                        className="input-field"
                    value={newRate.agentId}
                    onChange={(e) => setNewRate({ ...newRate, agentId: e.target.value })}
                      >
                    <option value="">Select Salesman</option>
                        {agents?.map((agent) => (
                          <option key={agent.id} value={agent.id}>
                        {agent.user ? `${agent.user.firstName} ${agent.user.lastName}` : `${agent.agentCode}`}
                          </option>
                    )) || []}
                      </select>
          </div>
                    )}

                    {/* Override Rate */}
                <div>
                      <label className="block text-sm font-medium text-accent-700 mb-1">Override Rate ($) *</label>
                  <input
                        type="number"
                    className="input-field"
                    value={newRate.overrideRate || ''}
                    onChange={(e) => setNewRate({ ...newRate, overrideRate: parseFloat(e.target.value) || 0 })}
                    placeholder="2"
                        min="0.10"
                    max="10"
                        step="0.01"
                      />
                  {newRate.overrideRate > 5 && (
                    <p className="text-yellow-600 text-xs mt-1">⚠️ Higher pay than average sales manager</p>
                      )}
                </div>
                </div>

              {/* Job Type and Override Tier Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Job Type - Show "Master Organization" for FMO CEO, dropdown for others */}
                    {user?.role === 'fmo_ceo' ? (
                <div>
                      <label className="block text-sm font-medium text-accent-700 mb-1">Job Type *</label>
                      <div className="flex items-center space-x-2 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
                        <Building className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-medium text-primary-900">Master Organization</span>
                      </div>
                </div>
                    ) : (
                <div>
                      <label className="block text-sm font-medium text-accent-700 mb-1">Job Type *</label>
                      <select
                    className="input-field"
                    value={newRate.jobType}
                    onChange={(e) => setNewRate({ ...newRate, jobType: e.target.value as any })}
                  >
                    <option value="SALES_AGENT">Sales Agent</option>
                    <option value="LEAD_GENERATION">Lead Generation</option>
                    <option value="EXECUTIVE_AGENT">Executive Agent</option>
                      </select>
                </div>
                    )}

                    {/* Enable Override Tier */}
                <div>
                  <label className="block text-sm font-medium text-accent-700 mb-1">Enable Override Tier *</label>
                  <select
                    className="input-field"
                    value={newRate.enableOverrideTier ? 'yes' : 'no'}
                    onChange={(e) => setNewRate({ ...newRate, enableOverrideTier: e.target.value === 'yes' })}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                  </select>
                  {newRate.enableOverrideTier && (
                    <p className="text-yellow-600 text-xs mt-1">⚠️ Selecting yes will bypass Override rate</p>
                      )}
                    </div>
                </div>

              {/* Volume Tiers Section */}
              <div className="mt-6">
                <h4 className="text-md font-semibold text-accent-900 mb-4">Volume Tiers Configuration</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* VOLUME TIER 1 */}
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <h5 className="font-medium text-accent-700 mb-3 text-center">VOLUME TIER 1</h5>
                    <div className="space-y-3">
                <div>
                        <label className="block text-sm font-medium text-accent-600 mb-1">Volume Range</label>
                          <div className="flex items-center space-x-2">
                  <input
                    type="text"
                              className="input-field text-sm w-24"
                            value={newRate.volumeTier1Min || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setNewRate({ ...newRate, volumeTier1Min: value === '' ? 0 : parseInt(value) });
                              }
                            }}
                              placeholder="1"
                            />
                            <span className="text-accent-500">to</span>
                            <input
                              type="text"
                              className="input-field text-sm w-24"
                            value={newRate.volumeTier1Max || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setNewRate({ ...newRate, volumeTier1Max: value === '' ? 0 : parseInt(value) });
                              }
                            }}
                              placeholder="300"
                  />
                </div>
                        <p className="text-xs text-accent-500 mt-1">(team sales count)</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-accent-600 mb-1">Tier1 Pay Rate ($)</label>
                    <input
                            type="text"
                            className="input-field text-sm"
                          value={newRate.tier1PayRate || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setNewRate({ ...newRate, tier1PayRate: value === '' ? 0 : parseFloat(value) || 0 });
                            }
                          }}
                          placeholder="2"
                          />
                          <p className="text-xs text-accent-500 mt-1">(default dollar amount)</p>
                      </div>
                </div>
              </div>

                  {/* VOLUME TIER 2 */}
                  <div className="border rounded-lg p-4 bg-green-50">
                    <h5 className="font-medium text-accent-700 mb-3 text-center">VOLUME TIER 2</h5>
                    <div className="space-y-3">
                        <div>
                        <label className="block text-sm font-medium text-accent-600 mb-1">Volume Range</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              className="input-field text-sm w-24"
                            value={newRate.volumeTier2Min || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setNewRate({ ...newRate, volumeTier2Min: value === '' ? 0 : parseInt(value) });
                              }
                            }}
                              placeholder="301"
                            />
                            <span className="text-accent-500">to</span>
                            <input
                              type="text"
                              className="input-field text-sm w-24"
                            value={newRate.volumeTier2Max || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setNewRate({ ...newRate, volumeTier2Max: value === '' ? 0 : parseInt(value) });
                              }
                            }}
                              placeholder="500"
                            />
              </div>
                        <p className="text-xs text-accent-500 mt-1">(team sales count)</p>
            </div>
                        <div>
                          <label className="block text-sm font-medium text-accent-600 mb-1">Tier2 Pay Rate ($)</label>
                <input
                            type="text"
                            className="input-field text-sm"
                          value={newRate.tier2PayRate || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setNewRate({ ...newRate, tier2PayRate: value === '' ? 0 : parseFloat(value) || 0 });
                            }
                          }}
                          placeholder="3"
                          />
                          <p className="text-xs text-accent-500 mt-1">(default dollar amount)</p>
                      </div>
                        </div>
              </div>

                  {/* VOLUME TIER 3 */}
                  <div className="border rounded-lg p-4 bg-purple-50">
                    <h5 className="font-medium text-accent-700 mb-3 text-center">VOLUME TIER 3</h5>
                    <div className="space-y-3">
              <div>
                        <label className="block text-sm font-medium text-accent-600 mb-1">Volume Range</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              className="input-field text-sm w-24"
                            value={newRate.volumeTier3Min || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setNewRate({ ...newRate, volumeTier3Min: value === '' ? 0 : parseInt(value) });
                              }
                            }}
                              placeholder="501"
                            />
                            <span className="text-accent-500">to</span>
                            <input
                              type="text"
                              className="input-field text-sm w-24"
                            value={newRate.volumeTier3Max || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setNewRate({ ...newRate, volumeTier3Max: value === '' ? 0 : parseInt(value) });
                              }
                            }}
                              placeholder="1000"
                            />
              </div>
                        <p className="text-xs text-accent-500 mt-1">(team sales count)</p>
                        </div>
              <div>
                          <label className="block text-sm font-medium text-accent-600 mb-1">Tier3 Pay Rate ($)</label>
                          <input
                            type="text"
                            className="input-field text-sm"
                          value={newRate.tier3PayRate || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setNewRate({ ...newRate, tier3PayRate: value === '' ? 0 : parseFloat(value) || 0 });
                            }
                          }}
                          placeholder="5"
                          />
                          <p className="text-xs text-accent-500 mt-1">(default dollar amount)</p>
              </div>
                      </div>
                    </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button onClick={() => setShowAddForm(false)} className="btn-outline">
                      Cancel
                    </button>
                    <button
                  onClick={handleAddRate}
                  className={`btn-primary flex items-center justify-center space-x-2 ${
                    isSubmitting 
                      ? 'opacity-75 cursor-not-allowed' 
                      : 'hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                  }`}
                  disabled={
                    !newRate.organizationId || 
                    !newRate.campaignId || 
                    (user?.role !== 'fmo_ceo' && !newRate.agentId) || 
                    isSubmitting
                  }
                    >
                      {isSubmitting ? (
                        <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Adding Override Rate...</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          <span>Add Override Rate</span>
                        </>
                      )}
                </button>
              </div>
            </div>
          </div>
        </div>
          )}

      {/* Statistics Cards */}
      

      {/* Search Filter */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search override rates..."
                className="input-field pl-10"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={clearFilters}
              className="btn-outline text-sm"
            >
              Clear Search
            </button>
          </div>
        </div>
      </div>

      {/* Override Rates Table */}
      <div className="card">
        {ratesLoading ? (
          <OverrideRateShimmerTable rows={itemsPerPage} />
        ) : (
          <div className="w-full max-h-[600px] overflow-auto">
              <table className="min-w-full table-auto">
                <thead className="bg-accent-50 sticky top-0 z-2">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Organization (EM Level)
                    </th>
                    {user?.role !== 'fmo_ceo' && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Agent
                    </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Campaign
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Override Rate
                    </th>
                    {user?.role !== 'fmo_ceo' && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Job Type
                    </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Override Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Volume Tiers
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                      Actions
                    </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-accent-200">
                {rates.map((rate) => (
                        <tr key={rate.id} className="hover:bg-accent-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building className="w-5 h-5 text-primary-600 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-accent-900">
                            {rate.organization?.name || 'N/A'}
                          </div>
                          <div className="text-xs text-accent-500">
                            {rate.organization?.type || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    {user?.role !== 'fmo_ceo' && (
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-accent-400 mr-2" />
                              <div>
                          <div className="text-sm font-medium text-accent-900">
                            {rate.agent?.user ? `${rate.agent.user.firstName} ${rate.agent.user.lastName}` : rate.agent?.agentCode || 'N/A'}
                          </div>
                          <div className="text-sm text-accent-500">
                            {rate.agent?.agentCode || ''}
                          </div>
                        </div>
                            </div>
                          </td>
                    )}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-accent-900">
                        {rate.campaign?.name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {editingRate === rate.id ? (
                        <input
                          type="number"
                          className="input-field w-24 text-sm"
                          value={editingData.overrideRate || ''}
                          onChange={(e) => setEditingData({ ...editingData, overrideRate: parseFloat(e.target.value) || 0 })}
                          min="0.10"
                          max="10"
                          step="0.01"
                        />
                      ) : (
                      <div className="text-sm font-medium text-accent-900">
                        {formatCurrency(rate.overrideRate)}
                        </div>
                      )}
                    </td>
                    {user?.role !== 'fmo_ceo' && (
                    <td className="px-4 py-4 whitespace-nowrap">
                      {editingRate === rate.id ? (
                        <select
                          className="input-field text-sm"
                          value={editingData.jobType || rate.jobType}
                          onChange={(e) => setEditingData({ ...editingData, jobType: e.target.value as any })}
                        >
                          <option value="SALES_AGENT">Sales Agent</option>
                          <option value="LEAD_GENERATION">Lead Generation</option>
                          <option value="EXECUTIVE_AGENT">Executive Agent</option>
                        </select>
                      ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getJobTypeLabel(rate.jobType)}
                      </span>
                      )}
                    </td>
                    )}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {editingRate === rate.id ? (
                        <select
                          className="input-field text-sm"
                          value={editingData.enableOverrideTier !== undefined ? (editingData.enableOverrideTier ? 'yes' : 'no') : (rate.enableOverrideTier ? 'yes' : 'no')}
                          onChange={(e) => setEditingData({ ...editingData, enableOverrideTier: e.target.value === 'yes' })}
                        >
                          <option value="no">Disabled</option>
                          <option value="yes">Enabled</option>
                        </select>
                      ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              rate.enableOverrideTier 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                        {rate.enableOverrideTier ? 'Enabled' : 'Disabled'}
                          </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {editingRate === rate.id ? (
                        <div className="space-y-2 min-w-[300px]">
                          {/* Tier 1 */}
                          <div className="flex items-center space-x-1 text-xs">
                            <span className="font-medium w-6">T1:</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.volumeTier1Min ?? rate.volumeTier1Min}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier1Min: (value === '' ? '' : parseInt(value)) as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier1Min: rate.volumeTier1Min 
                                  });
                                }
                              }}
                            />
                            <span>-</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.volumeTier1Max ?? rate.volumeTier1Max}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier1Max: (value === '' ? '' : parseInt(value)) as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier1Max: rate.volumeTier1Max 
                                  });
                                }
                              }}
                            />
                            <span>→ $</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.tier1PayRate ?? rate.tier1PayRate}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    tier1PayRate: (value === '' ? '' : parseFloat(value) || '') as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    tier1PayRate: rate.tier1PayRate 
                                  });
                                }
                              }}
                            />
                          </div>
                          {/* Tier 2 */}
                          <div className="flex items-center space-x-1 text-xs">
                            <span className="font-medium w-6">T2:</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.volumeTier2Min ?? rate.volumeTier2Min}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier2Min: (value === '' ? '' : parseInt(value)) as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier2Min: rate.volumeTier2Min 
                                  });
                                }
                              }}
                            />
                            <span>-</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.volumeTier2Max ?? rate.volumeTier2Max}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier2Max: (value === '' ? '' : parseInt(value)) as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier2Max: rate.volumeTier2Max 
                                  });
                                }
                              }}
                            />
                            <span>→ $</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.tier2PayRate ?? rate.tier2PayRate}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    tier2PayRate: (value === '' ? '' : parseFloat(value) || '') as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    tier2PayRate: rate.tier2PayRate 
                                  });
                                }
                              }}
                            />
                          </div>
                          {/* Tier 3 */}
                          <div className="flex items-center space-x-1 text-xs">
                            <span className="font-medium w-6">T3:</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.volumeTier3Min ?? rate.volumeTier3Min}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier3Min: (value === '' ? '' : parseInt(value)) as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier3Min: rate.volumeTier3Min 
                                  });
                                }
                              }}
                            />
                            <span>-</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.volumeTier3Max ?? rate.volumeTier3Max}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier3Max: (value === '' ? '' : parseInt(value)) as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    volumeTier3Max: rate.volumeTier3Max 
                                  });
                                }
                              }}
                            />
                            <span>→ $</span>
                            <input
                              type="text"
                              className="input-field w-16 text-xs py-1"
                              value={editingData.tier3PayRate ?? rate.tier3PayRate}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  setEditingData({ 
                                    ...editingData, 
                                    tier3PayRate: (value === '' ? '' : parseFloat(value) || '') as any
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingData({ 
                                    ...editingData, 
                                    tier3PayRate: rate.tier3PayRate 
                                  });
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                      <div className="text-sm text-accent-900">
                        <div>T1: {rate.volumeTier1Min}-{rate.volumeTier1Max} → {formatCurrency(rate.tier1PayRate)}</div>
                        <div>T2: {rate.volumeTier2Min}-{rate.volumeTier2Max} → {formatCurrency(rate.tier2PayRate)}</div>
                        <div>T3: {rate.volumeTier3Min}-{rate.volumeTier3Max} → {formatCurrency(rate.tier3PayRate)}</div>
                            </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {editingRate === rate.id ? (
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => setEditingData({ ...editingData, isActive: !(editingData.isActive ?? rate.isActive) })}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              (editingData.isActive ?? rate.isActive)
                                ? 'bg-green-600'
                                : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                (editingData.isActive ?? rate.isActive)
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className={`text-xs font-medium ${
                            (editingData.isActive ?? rate.isActive)
                              ? 'text-green-700'
                              : 'text-gray-700'
                          }`}>
                            {(editingData.isActive ?? rate.isActive) ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rate.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {rate.isActive ? 'Active' : 'Inactive'}
                      </span>
                      )}
                          </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {editingRate === rate.id ? (
                          <>
                          <button
                              onClick={() => handleSaveEdit(rate.id)}
                              className={`flex items-center justify-center space-x-1 px-3 py-1.5 rounded-md transition-colors ${
                                isSubmitting 
                                  ? 'bg-green-100 text-green-400 cursor-not-allowed' 
                                  : 'bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-900'
                              }`}
                              disabled={isSubmitting}
                              title={isSubmitting ? 'Updating...' : 'Save changes'}
                            >
                              {isSubmitting ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent"></div>
                                  <span className="text-xs">Saving...</span>
                                </>
                              ) : (
                                <>
                              <Save className="w-4 h-4" />
                                  <span className="text-xs">Save</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setEditingRate(null)
                                setEditingData({})
                                setValidationResult(null)
                              }}
                              className="flex items-center justify-center space-x-1 px-3 py-1.5 rounded-md bg-gray-50 text-accent-600 hover:bg-gray-100 hover:text-accent-700 transition-colors"
                              disabled={isSubmitting}
                              title="Cancel editing"
                            >
                              <X className="w-4 h-4" />
                              <span className="text-xs">Cancel</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditRate(rate)}
                              className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                              onClick={() => handleDeleteRate(rate.id)}
                              className="text-red-600 hover:text-red-900"
                              disabled={isSubmitting}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          </>
                        )}
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rates?.length === 0 && !ratesLoading && (
          <div className="text-center py-8">
            {!selectedCampaign ? (
              <>
                <Target className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-accent-500">Please select a campaign to view override rates.</p>
              </>
            ) : (
              <>
                <Building className="w-12 h-12 text-accent-300 mx-auto mb-4" />
                <p className="text-accent-500">No override rates found for {selectedCampaign.name}.</p>
                <button onClick={() => setShowAddForm(true)} className="btn-primary mt-4">
                  Add Override Rate
                </button>
              </>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-accent-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-accent-300 text-sm font-medium rounded-md text-accent-700 bg-white hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-accent-300 text-sm font-medium rounded-md text-accent-700 bg-white hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
      </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-accent-700">
                  Showing{' '}
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, totalItems)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{totalItems}</span>
                  {' '}results
                  </p>
                </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="input-field text-sm py-1"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                    </select>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    className="p-2 rounded-md text-accent-400 hover:text-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                    <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    className="p-2 rounded-md text-accent-400 hover:text-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                    <ChevronLeft className="w-4 h-4" />
                    </button>
                  <span className="px-3 py-2 text-sm text-accent-700">
                    Page {currentPage} of {totalPages}
                  </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    className="p-2 rounded-md text-accent-400 hover:text-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                    <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    className="p-2 rounded-md text-accent-400 hover:text-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                    <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>

      {/* Success Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right-full duration-300">
          <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">{toastMessage}</p>
              </div>
              <button 
                onClick={() => setToastMessage(null)}
                className="text-green-600 hover:text-green-800 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Toast Notification */}
      {errorToast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right-full duration-300">
          <div className="bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{errorToast}</p>
              </div>
              <button 
                onClick={() => setErrorToast(null)}
                className="text-red-600 hover:text-red-800 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}